import { useState, useEffect } from "react";
import {
  Calendar,
  X,
  Loader2,
  Users,
  UserPlus,
  Clock,
  ExternalLink,
  CheckCircle2,
  XCircle,
  Mail,
  Send,
  Video,
  Film,
  Download,
  Mic,
  Trash2,
} from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { Badge, Button } from "../../components/ui";
import VideoOverlayGenerator from "../../components/VideoOverlayGenerator";
import {
  fetchZoomEvent,
  fetchZoomRegistrations,
  fetchZoomInvitations,
  sendZoomInvitations,
  fetchZoomAttendance,
  fetchZoomRecordings,
  fetchZoomPanelists,
  fetchZoomPolls,
  cancelZoomEvent,
  fetchIntroVideoUrl,
  fetchVideoTemplate,
  sendHostCalendarInvite,
  deleteZoomInvitation,
} from "../../services/api";
import { STATUS_MAP, INVITATION_STATUS } from "./constants";
import QuickInvite from "./QuickInvite";
import PollsPanel from "./PollsPanel";
import PanelistsPanel from "./PanelistsPanel";
import ResendButton from "./ResendButton";
import ThumbnailUpload from "./ThumbnailUpload";
import InviteContactsModal from "./InviteContactsModal";
import PostEventEmailModal from "./PostEventEmailModal";
import ResourcesPanel from "./ResourcesPanel";

export default function EventDrawer({ event, onClose, onCancelled }) {
  const [detail, setDetail] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [panelists, setPanelists] = useState([]);
  const [polls, setPolls] = useState([]);
  const [attendance, setAttendance] = useState(null);
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showPostEvent, setShowPostEvent] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [tab, setTab] = useState("panelists");
  const [showVideoGen, setShowVideoGen] = useState(false);
  const [introVideoUrl, setIntroVideoUrl] = useState(null);
  const [templateUrl, setTemplateUrl] = useState(null);
  const [hostInviting, setHostInviting] = useState(false);
  const [hostInviteResult, setHostInviteResult] = useState(null);

  useEffect(() => {
    if (!event) return;
    setLoading(true);
    setTab("panelists");
    setIntroVideoUrl(null);
    setTemplateUrl(null);

    Promise.all([
      fetchZoomEvent(event.id).catch(() => ({ event })),
      fetchZoomRegistrations(event.id).catch(() => ({ registrations: [] })),
      fetchZoomInvitations(event.id).catch(() => ({ invitations: [] })),
      fetchZoomPanelists(event.id).catch(() => ({ panelists: [] })),
      fetchZoomPolls(event.id).catch(() => ({ polls: [] })),
      fetchZoomAttendance(event.id).catch(() => null),
      fetchZoomRecordings(event.id).catch(() => ({ recordings: [] })),
      fetchIntroVideoUrl(event.id).catch(() => ({ introVideoUrl: null })),
      fetchVideoTemplate().catch(() => ({ url: null })),
    ]).then(
      ([
        detailData,
        regData,
        invData,
        panData,
        pollData,
        attData,
        recData,
        introData,
        tplData,
      ]) => {
        setDetail(detailData.event || event);
        setRegistrations(regData.registrations || []);
        setInvitations(invData.invitations || []);
        setPanelists(panData.panelists || []);
        setPolls(pollData.polls || []);
        setAttendance(attData?.summary || null);
        setRecordings(recData.recordings || []);
        setIntroVideoUrl(introData.introVideoUrl || null);
        setTemplateUrl(tplData.url || null);
        setLoading(false);
      },
    );
  }, [event]);

  if (!event) return null;

  const ev = detail || event;
  const statusInfo = STATUS_MAP[ev.status] || STATUS_MAP.scheduled;
  const eventDate = new Date(ev.scheduledAt);

  const handleCancel = async () => {
    if (
      !window.confirm(
        `Cancel "${ev.title}"? This will also cancel the Zoom meeting.`,
      )
    )
      return;
    setCancelling(true);
    try {
      await cancelZoomEvent(ev.id);
      onCancelled(ev.id);
    } catch (err) {
      setCancelling(false);
      alert(`Failed to cancel: ${err.message}`);
    }
  };

  const handleHostInvite = async () => {
    setHostInviting(true);
    setHostInviteResult(null);
    try {
      const result = await sendHostCalendarInvite(ev.id);
      setHostInviteResult({ success: true, sentTo: result.sentTo });
    } catch (err) {
      setHostInviteResult({ error: err.message });
    } finally {
      setHostInviting(false);
    }
  };

  const refreshInvitations = () => {
    fetchZoomInvitations(ev.id)
      .then((data) => setInvitations(data.invitations || []))
      .catch(() => {});
  };

  const refreshPanelists = () => {
    fetchZoomPanelists(ev.id)
      .then((data) => setPanelists(data.panelists || []))
      .catch(() => {});
  };

  const refreshPolls = () => {
    fetchZoomPolls(ev.id)
      .then((data) => setPolls(data.polls || []))
      .catch(() => {});
  };

  const tabs = [
    { id: "panelists", label: "Panelists", count: panelists.length },
    { id: "polls", label: "Polls", count: polls.length },
    { id: "invitations", label: "Invitations", count: invitations.length },
    {
      id: "registrations",
      label: "Registrations",
      count: registrations.length,
    },
    { id: "resources", label: "Resources", count: (detail?.resources || []).length },
    { id: "attendance", label: "Attendance" },
    { id: "recordings", label: "Recordings", count: recordings.length },
  ];

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          zIndex: 99,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 520,
          background: colors.bgCard,
          borderLeft: `1px solid ${colors.border}`,
          zIndex: 100,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-20px 0 60px rgba(0,0,0,0.12)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px 28px",
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 12,
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <h2
                style={{
                  fontFamily: fonts.heading,
                  fontSize: 20,
                  color: colors.text,
                  margin: 0,
                  marginBottom: 6,
                }}
              >
                {ev.title}
              </h2>
              <Badge color={statusInfo.color} bg={statusInfo.bg}>
                {statusInfo.label}
              </Badge>
            </div>
            <X
              size={20}
              color={colors.textSecondary}
              style={{ cursor: "pointer", flexShrink: 0 }}
              onClick={onClose}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: 16,
              fontSize: 13,
              color: colors.textSecondary,
              marginTop: 12,
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Calendar size={13} />
              {eventDate.toLocaleDateString("en-GB", {
                weekday: "short",
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Clock size={13} />
              {eventDate.toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
              })}
              {ev.durationMinutes && ` (${ev.durationMinutes}m)`}
            </span>
          </div>

          {ev.description && (
            <p
              style={{
                fontSize: 13,
                color: colors.textSecondary,
                marginTop: 10,
                marginBottom: 0,
                lineHeight: 1.5,
              }}
            >
              {ev.description}
            </p>
          )}

          {/* Stats row */}
          <div style={{ display: "flex", gap: 16, marginTop: 14 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 12,
                color: colors.textSecondary,
              }}
            >
              <Mic size={13} /> {panelists.length} panelist
              {panelists.length !== 1 ? "s" : ""}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 12,
                color: colors.textSecondary,
              }}
            >
              <Mail size={13} /> {invitations.length} invited
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                fontSize: 12,
                color: colors.textSecondary,
              }}
            >
              <Users size={13} /> {registrations.length} registered
            </div>
          </div>

          {/* Intro Video */}
          {!loading && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 14,
              }}
            >
              {introVideoUrl ? (
                <>
                  <Film size={14} color={colors.success} />
                  <span style={{ fontSize: 12, color: colors.textSecondary }}>
                    Intro video ready
                  </span>
                  <a
                    href={introVideoUrl}
                    download={`${ev.title} - Intro.webm`}
                    style={{
                      fontSize: 12,
                      color: colors.info,
                      textDecoration: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                    }}
                  >
                    <Download size={12} /> Download
                  </a>
                  {templateUrl && (
                    <button
                      onClick={() => setShowVideoGen(true)}
                      style={{
                        fontSize: 12,
                        color: colors.textSecondary,
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                        textDecoration: "underline",
                      }}
                    >
                      Regenerate
                    </button>
                  )}
                </>
              ) : templateUrl && ev.status === "scheduled" ? (
                <Button
                  variant="default"
                  size="sm"
                  icon={Film}
                  onClick={() => setShowVideoGen(true)}
                >
                  Generate Intro Video
                </Button>
              ) : null}
            </div>
          )}

          {/* Thumbnail */}
          {!loading && (
            <ThumbnailUpload event={ev} onUpdated={(url) => setDetail({ ...ev, thumbnailUrl: url })} />
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
            {(ev.status === "scheduled" || ev.status === "draft") && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  icon={UserPlus}
                  onClick={() => setShowInvite(true)}
                >
                  Send Invitations
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  icon={hostInviting ? Loader2 : Calendar}
                  onClick={handleHostInvite}
                  disabled={hostInviting}
                >
                  {hostInviting ? "Sending..." : "Host Calendar Invite"}
                </Button>
                <Button variant="danger" size="sm" onClick={handleCancel}>
                  {cancelling ? "Cancelling..." : "Cancel Webcast"}
                </Button>
              </>
            )}
            {hostInviteResult && (
              <span style={{ fontSize: 12, alignSelf: "center", color: hostInviteResult.error ? colors.error : colors.success }}>
                {hostInviteResult.error || `Sent to ${hostInviteResult.sentTo}`}
              </span>
            )}
            {ev.status === "completed" && (
              <Button
                variant="primary"
                size="sm"
                icon={Send}
                onClick={() => setShowPostEvent(true)}
              >
                Send Highlights Email
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: "flex",
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                flex: 1,
                padding: "10px 0",
                background: "none",
                border: "none",
                borderBottom:
                  tab === t.id
                    ? `2px solid ${colors.accent}`
                    : "2px solid transparent",
                color: tab === t.id ? colors.text : colors.textSecondary,
                fontSize: 11,
                fontWeight: 600,
                fontFamily: fonts.body,
                cursor: "pointer",
              }}
            >
              {t.label}
              {t.count != null ? ` (${t.count})` : ""}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 28px" }}>
          {loading ? (
            <div
              style={{
                textAlign: "center",
                padding: 32,
                color: colors.textSecondary,
              }}
            >
              <Loader2
                size={20}
                className="spin"
              />
            </div>
          ) : tab === "panelists" ? (
            <PanelistsPanel
              event={ev}
              panelists={panelists}
              onRefresh={refreshPanelists}
            />
          ) : tab === "polls" ? (
            <PollsPanel event={ev} polls={polls} onRefresh={refreshPolls} />
          ) : tab === "invitations" ? (
            <>
              {(ev.status === "scheduled" || ev.status === "draft") && (
                <QuickInvite
                  event={ev}
                  onInvited={refreshInvitations}
                  existingEmails={
                    new Set(invitations.map((i) => i.email.toLowerCase()))
                  }
                />
              )}
              {(ev.status === "scheduled" || ev.status === "draft") && invitations.filter((i) => i.status === "invited").length > 0 && (
                <ResendButton eventId={ev.id} unregisteredCount={invitations.filter((i) => i.status === "invited").length} onResent={refreshInvitations} />
              )}
              {invitations.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: 32,
                    color: colors.textSecondary,
                    fontSize: 13,
                  }}
                >
                  No invitations sent yet. Click "Send Invitations" to invite
                  contacts.
                </div>
              ) : (
                invitations.map((inv) => {
                  const st =
                    INVITATION_STATUS[inv.status] || INVITATION_STATUS.invited;
                  return (
                    <div
                      key={inv.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "10px 0",
                        borderBottom: `1px solid ${colors.borderLight}`,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: colors.text,
                          }}
                        >
                          {[inv.firstName, inv.lastName]
                            .filter(Boolean)
                            .join(" ") || inv.email}
                        </div>
                        <div
                          style={{ fontSize: 12, color: colors.textSecondary }}
                        >
                          {inv.email}
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Badge color={st.color} bg={st.bg}>
                          {st.label}
                        </Badge>
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Remove invitation for ${inv.email}?`)) return;
                            try {
                              await deleteZoomInvitation(ev.id, inv.id);
                              refreshInvitations();
                            } catch (err) {
                              alert(`Failed to remove: ${err.message}`);
                            }
                          }}
                          title="Remove invitation"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: 4,
                            color: colors.danger,
                          }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </>
          ) : tab === "registrations" ? (
            registrations.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: 32,
                  color: colors.textSecondary,
                  fontSize: 13,
                }}
              >
                No registrations yet
              </div>
            ) : (
              registrations.map((reg) => (
                <div
                  key={reg.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 0",
                    borderBottom: `1px solid ${colors.borderLight}`,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: colors.text,
                      }}
                    >
                      {[reg.firstName, reg.lastName]
                        .filter(Boolean)
                        .join(" ") || reg.email}
                    </div>
                    <div style={{ fontSize: 12, color: colors.textSecondary }}>
                      {reg.email}
                      {reg.source && (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: 10,
                            color: colors.textSecondary,
                          }}
                        >
                          ({reg.source})
                        </span>
                      )}
                    </div>
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 6 }}
                  >
                    {reg.status === "attended" && (
                      <CheckCircle2 size={14} color={colors.success} />
                    )}
                    {reg.status === "no_show" && (
                      <XCircle size={14} color={colors.danger} />
                    )}
                    {reg.status === "cancelled" && (
                      <XCircle size={14} color={colors.textSecondary} />
                    )}
                    {reg.status === "registered" && (
                      <CheckCircle2 size={14} color={colors.info} />
                    )}
                    <span
                      style={{
                        fontSize: 11,
                        color: colors.textSecondary,
                        textTransform: "capitalize",
                      }}
                    >
                      {reg.status}
                    </span>
                  </div>
                </div>
              ))
            )
          ) : tab === "resources" ? (
            <ResourcesPanel event={detail || event} onRefresh={() => fetchZoomEvent(ev.id).then(d => setDetail(d.event || d))} />
          ) : tab === "attendance" ? (
            attendance ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 12,
                }}
              >
                {[
                  {
                    label: "Registered",
                    value: attendance.registered,
                    color: colors.info,
                  },
                  {
                    label: "Attended",
                    value: attendance.attended,
                    color: colors.success,
                  },
                  {
                    label: "No Show",
                    value: attendance.noShow,
                    color: colors.warning,
                  },
                  {
                    label: "Cancelled",
                    value: attendance.cancelled,
                    color: colors.textSecondary,
                  },
                ].map((s) => (
                  <div
                    key={s.label}
                    style={{
                      padding: 16,
                      borderRadius: 8,
                      border: `1px solid ${colors.border}`,
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 24,
                        fontWeight: 700,
                        fontFamily: fonts.heading,
                        color: s.color,
                      }}
                    >
                      {s.value}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: colors.textSecondary,
                        marginTop: 4,
                      }}
                    >
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: 32,
                  color: colors.textSecondary,
                  fontSize: 13,
                }}
              >
                Attendance data not yet available
              </div>
            )
          ) : recordings.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: 32,
                color: colors.textSecondary,
                fontSize: 13,
              }}
            >
              No recordings available
            </div>
          ) : (
            recordings.map((rec) => (
              <div
                key={rec.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 0",
                  borderBottom: `1px solid ${colors.borderLight}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Video size={14} color={colors.info} />
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: colors.text,
                        textTransform: "capitalize",
                      }}
                    >
                      {(rec.type || "recording").replace(/_/g, " ")}
                    </div>
                    <div style={{ fontSize: 11, color: colors.textSecondary }}>
                      {rec.fileExtension?.toUpperCase()}
                      {rec.fileSize
                        ? ` — ${(rec.fileSize / 1048576).toFixed(1)} MB`
                        : ""}
                    </div>
                  </div>
                </div>
                {rec.playUrl && (
                  <a
                    href={rec.playUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      color: colors.info,
                      fontSize: 12,
                      textDecoration: "none",
                    }}
                  >
                    Play <ExternalLink size={12} />
                  </a>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {showInvite && (
        <InviteContactsModal
          event={ev}
          onClose={() => setShowInvite(false)}
          onInvited={() => {
            refreshInvitations();
            setShowInvite(false);
          }}
        />
      )}
      {showPostEvent && (
        <PostEventEmailModal
          event={ev}
          recordings={recordings}
          onClose={() => setShowPostEvent(false)}
          onSent={() => setShowPostEvent(false)}
        />
      )}
      {showVideoGen && templateUrl && (
        <VideoOverlayGenerator
          templateUrl={templateUrl}
          title={ev.title}
          eventId={ev.id}
          onComplete={({ videoUrl }) => {
            setIntroVideoUrl(videoUrl);
            setShowVideoGen(false);
          }}
          onClose={() => setShowVideoGen(false)}
        />
      )}
    </>
  );
}
